import {Component} from 'panel';
import isEmpty from 'lodash/isEmpty';
import differenceBy from 'lodash/differenceBy';
import groupBy from 'lodash/groupBy';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';

import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {Permission} from 'common/report/constants';
import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {userDetailsStore} from 'common/report/stores/user-details';
import {currentProjectStore} from 'common/report/stores/project-details';
import {KEY_VALUES} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {
  allowStaffOverride,
  isAllowedSuperadminOverride,
  isSharingEnabledForProject,
} from 'common/report/util/saved-entities';
import {promptSuperadminOverrideSave} from 'common/report/util/saved-entities';
import {EntityType, OverrideModal} from 'common/report/util/saved-entities/types';
import {capitalize, pluralize} from 'common/util/string';

import template from './index.jade';
import css from './index.styl?inline';

const YOUR_DASHBOARDS_SECTION = `Your dashboards`;
const SHARED_WITH_YOU_SECTION = `Shared with you`;
const ALL_DASHBOARDS_SECTION = `All dashboards`;

export class DashboardSelector extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'create-dashboard-emits-event': `boolean`,
      'disable-create-dashboard': `boolean`,
      'enable-management': `boolean`,
      multiselect: `boolean`,
      open: `boolean`,
      'search-placeholder': {type: `string`, default: `Save to dashboard...`},
      'user-id': {type: `number`, description: `User ID of the user viewing selector`},
    };
  }

  constructor() {
    super(...arguments);
    this._controllerListener = this.updateFromController.bind(this);
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        activeItem: null, // active means highlighted, triggered by mouseover or arrow keys
        busy: false,
        creating: false,
        dashboards: [],
        disableCreateDashboard: false,
        loaded: false,
        newTitle: ``,
        open: false,
        scrollToId: null,
        searchFilter: ``,
        sections: [],
        selectionChanges: new Set(),
      },
      helpers: {
        handleMenuChange: async (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(`menuchange`, {detail: ev.detail}));

          if (ev.detail.state !== `closed`) {
            // menu opening
            this.focusSearch();
            if (this.controller && !this.controller.getLoaded()) {
              await this.controller.loadDashboards();
            }
          } else {
            // menu closing
            this.reset();
          }
        },
        handleSearchInput: (ev) => {
          const sections = this.getSections(this.state.dashboards, ev.target.value);
          this.update({
            searchFilter: ev.target.value,
            sections,
          });
          this.activateItemIfNeeded(sections);
        },
        handleSearchInputInsertHook: (vnode) => {
          this.searchInputEl = vnode.elm;
        },
        handleCreateInputInsertHook: (vnode) => {
          this.createInputEl = vnode.elm;
        },
        handleManageClick: () => this.dispatchEvent(new CustomEvent(`manage`)),
        handleCreateClick: () => {
          if (this.attr(`create-dashboard-emits-event`)) {
            this.dispatchEvent(new CustomEvent(`create`));
          } else {
            this.update({creating: true, newTitle: this.state.searchFilter});
            this.focusCreateField();
          }
        },
        handleCreateInput: (ev) => {
          this.update({newTitle: ev.target.value});
        },
        handleCreateSubmit: async () => {
          const {newTitle} = this.state;
          if (!isEmpty(newTitle)) {
            this.update({busy: true});
            const dashboard = await this.controller.createDashboard({
              title: newTitle,
            });
            this.dispatchEvent(new CustomEvent(`create`, {detail: dashboard}));
            this.update({busy: false, creating: false});
          }
        },
        handleApplyClick: async () => {
          const {dashboards, selectionChanges} = this.state;
          const addedDashboards = dashboards.filter(
            (dashboard) =>
              selectionChanges.has(dashboard.id) && !this.controller.bookmarkContainsDashboardId(dashboard.id),
          );
          const removedDashboards = dashboards.filter(
            (dashboard) =>
              selectionChanges.has(dashboard.id) && this.controller.bookmarkContainsDashboardId(dashboard.id),
          );
          const changes = {
            added: addedDashboards.map((dashboard) => dashboard.id),
            removed: removedDashboards.map((dashboard) => dashboard.id),
          };
          // If a project owner selects dashboards that are they don't have editor access
          // show override modal
          let allowAdd = true;
          const superAdminOverrideDashboards = addedDashboards.filter(isAllowedSuperadminOverride);
          if (superAdminOverrideDashboards.length) {
            const modalOutcome = await promptSuperadminOverrideSave(EntityType.Dashboard, {
              isBulkAction: true,
              selectedEntities: addedDashboards,
              selectedSuperadminOverrideEntities: superAdminOverrideDashboards,
            });
            allowAdd = modalOutcome === OverrideModal.Save;
          }
          if (allowAdd) {
            if (changes.added.length === 1 && changes.removed.length === 0) {
              addToast({
                message: `Added to 1 dashboard`,
                type: ToastType.SUCCESS,
                cta: `View Dashboard`,
                ctaCallback: () =>
                  window.open(
                    getReportUrl(`dashboards`, {projectId: getProjectId(), workspaceId: getWorkspaceId()}) +
                      `#id=${changes.added[0]}`,
                  ),
              });
            } else if (changes.removed.length === 1 && changes.added.length === 0) {
              addToast({
                message: `Removed from 1 dashboard`,
                type: ToastType.SUCCESS,
                cta: `View Dashboard`,
                ctaCallback: () =>
                  window.open(
                    getReportUrl(`dashboards`, {projectId: getProjectId(), workspaceId: getWorkspaceId()}) +
                      `#id=${changes.removed[0]}`,
                  ),
              });
            } else if (changes.removed.length > 0 || changes.added.length > 0) {
              const addedMessage = `added to ${changes.added.length} ${pluralize(`dashboard`, changes.added.length)}`;
              const removedMessage = `removed from ${changes.removed.length} ${pluralize(
                `dashboard`,
                changes.removed.length,
              )}`;
              let message;
              if (changes.added.length > 0 && changes.removed.length > 0) {
                message = `${capitalize(addedMessage)} & ${removedMessage}`;
              } else if (changes.added.length > 0) {
                message = capitalize(addedMessage);
              } else {
                message = capitalize(removedMessage);
              }
              addToast({
                message,
                type: ToastType.SUCCESS,
              });
            }

            this.dispatchEvent(new CustomEvent(`multiselect`, {detail: changes}));
            await this.controller.changeBookmarkDashboards(changes);
            this.update({selectionChanges: new Set()});
          }
        },
        handleItemRowInsertHook: (vnode) => {
          const itemRowEl = vnode.elm;
          if (itemRowEl.offsetParent && vnode.key === this.state.scrollToId) {
            this.update({scrollToId: null});
            window.requestAnimationFrame(() => {
              // under test the item row sometimes no longer exists
              if (itemRowEl?.offsetParent) {
                itemRowEl.offsetParent.scrollTop =
                  itemRowEl.offsetTop - itemRowEl.offsetParent.clientHeight + itemRowEl.offsetHeight;
              }
            });
          }
        },
        getChecked: (item) => {
          const hasBookmark = this.controller.bookmarkContainsDashboardId(item.dashboard.id);
          return this.state.selectionChanges.has(item.dashboard.id) ? !hasBookmark : hasBookmark;
        },
        isSharingEnabled: () => {
          return isSharingEnabledForProject(currentProjectStore.value);
        },
        getCheckDisabled: (item) => {
          // TODO: junhouse implement break glass UX for sharing enabled projects
          return (
            // eslint-disable-next-line camelcase
            !(item.dashboard?.can_update_basic || item.dashboard?.canUpdateBasic) &&
            !isAllowedSuperadminOverride(item.dashboard)
          );
        },
        // TODO opslead
        // eslint-disable-next-line sonarjs/cognitive-complexity
        getItemActionHandler: (item) => {
          if (this.state.multiselect) {
            if (this.helpers.getCheckDisabled(item)) {
              return () => {};
            } else {
              return () => {
                const {selectionChanges} = this.state;
                if (selectionChanges.has(item.dashboard.id)) {
                  selectionChanges.delete(item.dashboard.id);
                } else {
                  selectionChanges.add(item.dashboard.id);
                }
                this.update({selectionChanges});
                if (this.helpers.getChecked(item)) {
                  this.dispatchEvent(
                    new CustomEvent(`dashboardadded`, {detail: this.getVisibilityTrackingParams(item.dashboard)}),
                  );
                }
              };
            }
          } else {
            return async () => {
              const shouldChangeDashboard = this.dispatchEvent(
                new CustomEvent(`select`, {
                  detail: this.getVisibilityTrackingParams(item.dashboard),
                  cancelable: true,
                }),
              );
              if (shouldChangeDashboard) {
                await this.controller.changeActiveDashboard(item.dashboard.id);
                this.dispatchEvent(new CustomEvent(`dashboardchanged`, {detail: {id: item.dashboard.id}}));
              }
            };
          }
        },
        getItemMouseOverHandler: (item) => {
          return () => this.update({activeItem: item});
        },
      },
    };
  }

  getVisibilityTrackingParams(dashboard) {
    return {
      id: dashboard.id,
      userId: dashboard.creator_id,
    };
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    switch (attr) {
      case `multiselect`:
      case `open`:
        this.update({[attr]: this.attr(attr)});
        break;
      case `disable-create-dashboard`:
        this.update({disableCreateDashboard: this.attr(attr)});
        break;
    }
  }

  scrollActiveItemIntoView() {
    const itemRowEl = this.el.querySelector(`.mp-dashboard-selector-active`);
    const bottomScrollOffset = itemRowEl.offsetTop - itemRowEl.offsetParent.clientHeight + itemRowEl.offsetHeight;
    if (itemRowEl.offsetParent.scrollTop > itemRowEl.offsetTop) {
      itemRowEl.offsetParent.scrollTop = itemRowEl.offsetTop;
    } else if (itemRowEl.offsetParent.scrollTop < bottomScrollOffset) {
      itemRowEl.offsetParent.scrollTop = bottomScrollOffset;
    }
  }

  keyNavigateActiveItem(key) {
    const {activeItem, sections} = this.state;
    if (activeItem) {
      const sectionIdx = sections.findIndex((s) => s.items.some((it) => it.dashboard.id === activeItem.dashboard.id));
      const itemIdx = sections[sectionIdx].items.findIndex((it) => it.dashboard.id === activeItem.dashboard.id);

      let newActiveItem = null;
      switch (key) {
        case KEY_VALUES.ARROW_UP: {
          newActiveItem =
            sections[sectionIdx].items[itemIdx - 1] || // previous item in section
            (sectionIdx > 0 && [...sections[sectionIdx - 1].items].pop()) || // or last item in previous section
            null;
          break;
        }
        case KEY_VALUES.ARROW_DOWN: {
          newActiveItem =
            sections[sectionIdx].items[itemIdx + 1] || // next item in section
            (sectionIdx < sections.length - 1 && sections[sectionIdx + 1].items[0]) || // or first item in next section
            null;
          break;
        }
        default: {
          throw new Error(`Invalid navigation key`);
        }
      }
      if (newActiveItem) {
        this.update({activeItem: newActiveItem});
      }
    } else {
      this.update({activeItem: sections[0].items[0]});
    }
    window.requestAnimationFrame(() => this.scrollActiveItemIntoView());
  }

  connectedCallback() {
    super.connectedCallback(...arguments);
    this.currentProjectStoreCallback = () => this.update();
    currentProjectStore.subscribe(this.currentProjectStoreCallback).fetch();

    this._handleKeyDown = (ev) => {
      switch (ev.key) {
        case KEY_VALUES.ARROW_DOWN:
        case KEY_VALUES.ARROW_UP: {
          ev.preventDefault();
          this.keyNavigateActiveItem(ev.key);
          window.requestAnimationFrame(() => this.scrollActiveItemIntoView());
          break;
        }
        case KEY_VALUES.ENTER: {
          if (this.state.activeItem) {
            this.helpers.getItemActionHandler(this.state.activeItem)();
          }
          break;
        }
      }
    };
    this.addEventListener(`keydown`, this._handleKeyDown);
  }

  disconnectedCallback() {
    currentProjectStore.unsubscribe(this.currentProjectStoreCallback);
    this.removeEventListener(`keydown`, this._handleKeyDown);
    this.controller = null; // trigger unsubscribe in controller setter
    super.disconnectedCallback(...arguments);
  }

  reset() {
    this.update(
      pick(this.config.defaultState, [`activeItem`, `creating`, `newTitle`, `searchFilter`, `selectionChanges`]),
    );
    this.updateFromController();
  }

  focusSearch() {
    window.requestAnimationFrame(() => this.searchInputEl.focus());
  }

  focusCreateField() {
    window.requestAnimationFrame(() => {
      this.createInputEl.focus();
      this.createInputEl.select();
    });
  }

  getDashboardSection(dashboard) {
    // TODO: once sharing goes to GA removes the Feature Flag check
    if (this.isYourDashboard(dashboard)) {
      return YOUR_DASHBOARDS_SECTION;
    } else if (this.helpers.isSharingEnabled() && dashboard.can_view) {
      return SHARED_WITH_YOU_SECTION;
    } else {
      return ALL_DASHBOARDS_SECTION;
    }
  }

  getSectionIndex(section) {
    switch (section) {
      case YOUR_DASHBOARDS_SECTION:
        return 1;
      case SHARED_WITH_YOU_SECTION:
        return 2;
      case ALL_DASHBOARDS_SECTION:
        return 3;
      default:
        return Number.MAX_VALUE;
    }
  }

  getSections(dashboards, searchFilter) {
    // TODO: once sharing is enabled globally remove the feature flag check
    if (!this.helpers.isSharingEnabled()) {
      dashboards = dashboards.filter((dashboard) => dashboard.can_view);
    }

    const items = dashboards
      .map((dashboard) => {
        const titleMatch = fuzzyMatch(dashboard.title, searchFilter) || {matchParts: null, score: 0};
        const descMatch = fuzzyMatch(dashboard.description, searchFilter) || {matchParts: null, score: 0};

        return {
          titleSearchTokens: titleMatch.matchParts,
          descriptionSearchTokens: descMatch.matchParts,
          matchScore: Math.max(titleMatch.score, descMatch.score),
          dashboard,
        };
      })
      .filter((item) => item.titleSearchTokens || item.descriptionSearchTokens)
      .sort((a, b) => b.matchScore - a.matchScore);

    const sectionMap = groupBy(items, (item) => this.getDashboardSection(item.dashboard));
    const isSuperAdmin = userDetailsStore.value.projects[getProjectId()]?.permissions.includes(
      Permission.EditLockedEntity,
    );

    if (this.helpers.isSharingEnabled()) {
      if (!isSuperAdmin && !allowStaffOverride(getProjectId())) {
        delete sectionMap[ALL_DASHBOARDS_SECTION];
      }
    } else {
      delete sectionMap[SHARED_WITH_YOU_SECTION];
    }

    return sortBy(Object.entries(sectionMap), ([section]) => this.getSectionIndex(section))
      .map(([section, items]) => ({
        title: section,
        items,
      }))
      .filter((section) => section.items.length > 0);
  }

  isYourDashboard(dashboard) {
    return dashboard.creator_id === this.attr(`user-id`);
  }

  activateItemIfNeeded(sections) {
    const {activeItem} = this.state;
    const hasActiveItem =
      activeItem && sections.some((s) => s.items.some((it) => it.dashboard.id === activeItem.dashboard.id));
    if (!hasActiveItem) {
      this.update({activeItem: sections.length > 0 ? sections[0].items[0] : null});
    }
  }

  updateFromController() {
    const dashboards = this.controller.getDashboards();
    const added = differenceBy(dashboards, this.state.dashboards, `id`);
    if (this.state.dashboards && added.length > 0) {
      this.update({scrollToId: added[0].id});
    }

    const sections = this.getSections(dashboards || [], this.state.searchFilter);
    this.update({
      dashboards,
      sections,
    });
    this.activateItemIfNeeded(sections);
  }

  get controller() {
    return this.state.controller;
  }

  set controller(value) {
    if (this.state.controller) {
      this.unregisterController(this.state.controller);
    }
    this.update({controller: value});
    if (value) {
      this.registerController(value);
      this.updateFromController();
    }
  }

  registerController(controller) {
    controller.subscribeUpdates(this._controllerListener);
  }

  unregisterController(controller) {
    controller.unsubscribeUpdates(this._controllerListener);
  }
}

export const DashboardSelectorEl = defineAndMakeJsxFactory(`mp-dashboard-selector`, DashboardSelector);
